import moment from 'moment-timezone';
import { useQuery } from '@tanstack/react-query';
import useExpress from '../../../../repository/react-query/useExpress';
import { PRODUCER_BUSINESS_PAY_REGIONS } from '../../../../constants/region-utils';
import { getProducerBusinessesQuery } from '../../../../repository/queries';
import { getUser } from '../../../../../utils';
import axios from 'axios';

const getPaymentLineItems = (startDate, endDate) => {
    const endpoint = '/payment-line-items/config-line-items';
    const requests = [];
    /**
     * Batch PaymentLineItem requests into 1 month intervals to speed up page load.
     * Users of this function should ensure datepickers do not exceed a 1 year timeframe to limit loading times.
     */
    if (startDate && endDate) {
        let currentMonth = moment(startDate).startOf('month');
        while (currentMonth.isSameOrBefore(moment(endDate).startOf('month'))) {
            const params = {
                filter: {
                    deleted_at: { $eq: null },
                    start_date: currentMonth.toISOString(),
                    end_date: currentMonth.clone().endOf('month').toISOString(),
                },
                populate: {
                    payee_id: ['name', 'role', 'license_number', 'hauler_number', 'assignee_name', 'assignee_number', 'payment_method', 'classification_code'],
                    payer_id: ['name', 'role', 'license_number', 'hauler_number'],
                },
            };
            requests.push(useExpress({ endpoint, params }));
            currentMonth.add(1, 'month');
        }
    } else {
        const params = {
            filter: { deleted_at: { $eq: null } },
            populate: {
                payee_id: ['name', 'role', 'license_number', 'hauler_number', 'assignee_name', 'assignee_number', 'payment_method', 'classification_code'],
                payer_id: ['name', 'role', 'license_number', 'hauler_number'],
            },
        };
        requests.push(useExpress({ endpoint, params }));
    }

    /**
     * This enhances payment line items with producer business data for regions that support it.
     * Only fetches producer businesses for the specific producer IDs that are payers.
     */
    return async function () {
        const user = getUser();
        const responses = await Promise.all(requests.map((request) => request()));
        const paymentLineItems = responses.flat();

        // If the region doesn't support producer business, return items as-is
        if (!PRODUCER_BUSINESS_PAY_REGIONS.includes(user.region)) {
            return paymentLineItems;
        }

        try {
            // Extract unique producer IDs from payers
            const producerIds = [...new Set(paymentLineItems.filter((item) => item.payer_id?.role === 'producer').map((item) => item.payer_id._id.toString()))];

            // If no producers found, return items with empty producer business fields
            if (producerIds.length === 0) {
                return paymentLineItems.map((item) => ({
                    ...item,
                    producer_business_name: '',
                    producer_business_number: '',
                }));
            }

            // Fetch producer businesses
            const producerBusinessQuery = getProducerBusinessesQuery({
                filterDeleted: true,
            });
            const response = await axios.get(producerBusinessQuery);
            const producerBusinesses = response.data;

            // Create a map for efficient lookup
            const producerBusinessMap = new Map();
            producerBusinesses.forEach((business) => {
                business.producers?.forEach((producer) => {
                    const producerId = producer.toString();
                    if (producerIds.includes(producerId)) {
                        producerBusinessMap.set(producerId, {
                            name: business.name,
                            license_number: business.license_number,
                        });
                    }
                });
            });

            // Enhance line items with producer business data
            return paymentLineItems.map((item) => {
                // Check if payer is a producer and find matching producer business
                if (item.payer_id?.role === 'producer') {
                    const producerBusiness = producerBusinessMap.get(item.payer_id._id.toString());

                    if (producerBusiness) {
                        return {
                            ...item,
                            producer_business_name: producerBusiness.name,
                            producer_business_number: producerBusiness.license_number,
                        };
                    }
                }

                return {
                    ...item,
                    producer_business_name: '',
                    producer_business_number: '',
                };
            });
        } catch (error) {
            // If producer business fetching fails, return original items with empty producer business fields
            return paymentLineItems.map((item) => ({
                ...item,
                producer_business_name: '',
                producer_business_number: '',
            }));
        }
    };
};

const getPaymentLineItemRates = () => {
    const params = { filter: { deleted_at: { $eq: null } } };

    const endpoint = '/payment-line-item-rate';
    const request = useExpress({ endpoint, params });

    return async function () {
        const paymentLineItemRates = await request();
        return addReadableUniqueKeyToRates(paymentLineItemRates);
    };
};

const getFixedPaymentLineItemRates = () => {
    const params = { filter: { deleted_at: { $eq: null } } };

    const endpoint = '/payment-line-item-rate/fixed-rates';
    const request = useExpress({ endpoint, params });

    return async function () {
        const paymentLineItemRates = await request();
        return addReadableUniqueKeyToRates(paymentLineItemRates);
    };
};

const addReadableUniqueKeyToRates = (paymentLineItemRates) => {
    const counts = {};
    return paymentLineItemRates.map((paymentLineItemRate) => {
        const key = paymentLineItemRate.rate_name.replace(/\s/g, '');
        counts[key] = (counts[key] || 0) + 1;

        const unique_id = counts[key] > 1 ? `#${paymentLineItemRate.rate_name}${counts[key]}` : `#${paymentLineItemRate.rate_name}`;

        return {
            ...paymentLineItemRate,
            unique_id,
        };
    });
};

export const usePaymentLineItems = ({ startDate, endDate, enabled = true } = {}) => {
    const queryFn = getPaymentLineItems(startDate, endDate);
    const queryKey = ['payment-line-items', startDate, endDate];
    return useQuery({ queryKey, queryFn, enabled });
};

export const usePaymentLineItemRates = ({ enabled = true } = {}) => {
    const queryFn = getPaymentLineItemRates();
    const queryKey = ['payment-line-item-rate'];
    return useQuery({ queryKey, queryFn, enabled });
};

export const useFixedPaymentLineItemRates = ({ enabled = true } = {}) => {
    const queryFn = getFixedPaymentLineItemRates();
    const queryKey = ['payment-line-item-rate', 'fixed'];
    return useQuery({ queryKey, queryFn, enabled });
};
